import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const ServiceReminderList = () => {
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";
  const navigate = useNavigate();

  // Calculate default date range: last 15 days to next 60 days
  const getDefaultDateRange = () => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(today.getDate() - 15); // 15 days ago

    const toDate = new Date(today);
    toDate.setDate(today.getDate() + 60); // 60 days from now (for reminders)

    return {
      from_date: fromDate.toISOString().split("T")[0],
      to_date: toDate.toISOString().split("T")[0],
    };
  };

  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredReminders, setFilteredReminders] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [sentLoading, setSentLoading] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    reminder: null,
  });
  const [sentModal, setSentModal] = useState({
    isOpen: false,
    reminder: null,
  });

  // Date filters
  const [dateFilters, setDateFilters] = useState(getDefaultDateRange());
  const [isDefaultRange, setIsDefaultRange] = useState(true);

  useEffect(() => {
    fetchServiceReminders();
  }, []);

  useEffect(() => {
    if (searchText.trim() == "") {
      setFilteredReminders(reminders);
    } else {
      const filtered = reminders.filter(
        (reminder) =>
          (reminder.vehicle_reg_no || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (reminder.customer_name || "")
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (reminder.customer_mobile || "").includes(searchText) ||
          (reminder.service_reminder_remark || "")
            .toLowerCase()
            .includes(searchText.toLowerCase())
      );
      setFilteredReminders(filtered);
    }
  }, [searchText, reminders]);

  const fetchServiceReminders = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("reminder_grage_id", grage_id);
      formData.append("from_date", dateFilters.from_date);
      formData.append("to_date", dateFilters.to_date);

      const response = await axios.post(
        `${server_url}service_reminder_list.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        setReminders(response.data.result || []);
        setFilteredReminders(response.data.result || []);
      } else if (response.data.status == "0") {
        // No data found for this date range
        setReminders([]);
        setFilteredReminders([]);
        // Don't show error toast for no data
        if (response.data.error !== "No data found") {
          toast.error(
            response.data.error || "Failed to fetch service reminders"
          );
        }
      } else {
        toast.error(response.data.error || "Failed to fetch service reminders");
        setReminders([]);
        setFilteredReminders([]);
      }
    } catch (error) {
      console.error("Fetch service reminders error:", error);
      toast.error("Unable to fetch service reminders. Please try again.");
      setReminders([]);
      setFilteredReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    fetchServiceReminders();
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsDefaultRange(false);
  };

  const handleResetToDefault = () => {
    const defaultRange = getDefaultDateRange();
    setDateFilters(defaultRange);
    setIsDefaultRange(true);
  };

  const handleEditReminder = (reminder) => {
    navigate("/edit-service-reminder", { state: { reminder } });
  };

  const handleAddReminder = () => {
    navigate("/add-service-reminder");
  };

  const handleMarkAsSent = async (reminderId) => {
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("service_reminder_id", reminderId);
      formData.append("service_reminder_status", "2"); // 2 = sent

      const response = await axios.post(
        `${server_url}update_service_reminder.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        toast.success("Reminder marked as sent!");
        // Update local state
        setReminders((prev) =>
          prev.map((reminder) =>
            reminder.service_reminder_id == reminderId
              ? { ...reminder, service_reminder_status: "2" }
              : reminder
          )
        );
      } else {
        toast.error(response.data.error || "Failed to update reminder");
      }
    } catch (error) {
      console.error("Mark as sent error:", error);
      toast.error("Unable to update reminder. Please try again.");
    }
  };

  const openDeleteModal = (reminder) => {
    setDeleteModal({ isOpen: true, reminder });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, reminder: null });
  };

  const confirmDeleteReminder = async () => {
    const reminder = deleteModal.reminder;
    if (!reminder) return;

    setDeleteLoading(reminder.service_reminder_id);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("service_reminder_id", reminder.service_reminder_id);

      const response = await axios.post(
        `${server_url}delete_service_reminder.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1") {
        toast.success("Service reminder deleted successfully!");
        setReminders((prev) =>
          prev.filter(
            (i) => i.service_reminder_id !== reminder.service_reminder_id
          )
        );
        setFilteredReminders((prev) =>
          prev.filter(
            (i) => i.service_reminder_id !== reminder.service_reminder_id
          )
        );
        closeDeleteModal();
      } else {
        toast.error(response.data.error || "Failed to delete reminder");
      }
    } catch (error) {
      console.error("Delete reminder error:", error);
      toast.error("Unable to delete reminder. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const openSentModal = (reminder) => {
    setSentModal({ isOpen: true, reminder });
  };

  const closeSentModal = () => {
    setSentModal({ isOpen: false, reminder: null });
  };

  const confirmMarkAsSent = async () => {
    const reminder = sentModal.reminder;
    if (!reminder) return;

    setSentLoading(reminder.service_reminder_id);
    try {
      await handleMarkAsSent(reminder.service_reminder_id);
      closeSentModal();
    } catch (error) {
      // Error handling is already in handleMarkAsSent
    } finally {
      setSentLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const getStatusColor = (status, reminderDate) => {
    const today = new Date();
    const reminderDateObj = new Date(reminderDate);

    if (status == "2") return "#10b981"; // Sent - Green

    const diffTime = reminderDateObj - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "#dc2626"; // Overdue - Red
    if (diffDays <= 7) return "#f59e0b"; // Due soon - Orange
    return "#3b82f6"; // Upcoming - Blue
  };

  const getStatusText = (status, reminderDate) => {
    if (status == "2") return "Sent";

    const today = new Date();
    const reminderDateObj = new Date(reminderDate);
    const diffTime = reminderDateObj - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "Overdue";
    if (diffDays <= 7) return "Due Soon";
    return "Upcoming";
  };

  const handleShareReminder = (reminder) => {
    const customerName = reminder.customer_name || "Customer";
    const vehicleNo = reminder.vehicle_reg_no || "Vehicle";
    const reminderDate = formatDate(reminder.service_reminder_date);
    const customerMobile = reminder.customer_mobile || "";
    const remark = reminder.service_reminder_remark || "";

    const message = `🔔 *Service Reminder*
    
Hello ${customerName}! 👋

This is a friendly reminder for your vehicle service:
🚗 *Vehicle:* ${vehicleNo}
${remark ? `📝 *Remark:* ${remark}` : ""}

Please schedule your vehicle service before ${reminderDate}.

Best regards,
*${localStorage.getItem("grage_name") || "Our Garage"}*`;

    // For WhatsApp
    const whatsappUrl = `https://wa.me/${
      customerMobile ? `+91${customerMobile}` : ""
    }?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
    toast.success("Reminder shared via WhatsApp!");
  };

  return (
    <div className="history-container">
      {/* Sticky Header Section */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            <form onSubmit={handleSearch} className="search-form">
              <div className="input-group same-line">
                <input
                  type="date"
                  name="from_date"
                  value={dateFilters.from_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="From Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  name="to_date"
                  value={dateFilters.to_date}
                  onChange={handleDateFilterChange}
                  className="search-input"
                  placeholder="To Date"
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
                
              </div>
            </form>

            {/* Search Bar Below Date Filters */}
            <div style={{ marginTop: "10px" }}>
              <div className="input-group same-line">
                <input
                  type="text"
                  placeholder="Search by vehicle, customer, or mobile..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => e.key == "Enter" && handleSearch()}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="content-area servicereminderlist">
        {/* Service Reminder List */}
        <div className="home-vehicles">
          {loading ? (
            <div className="loading-vehicles">
              <div className="loading-spinner"></div>
              <div>Loading service reminders...</div>
            </div>
          ) : filteredReminders.length > 0 ? (
            <div className="vehicles-list home-icon">
              {filteredReminders.map((reminder) => (
                <div
                  key={reminder.service_reminder_id}
                  className="vehicle-card section-home"
                  style={{
                    borderLeft:
                      "4px solid " +
                      getStatusColor(
                        reminder.service_reminder_status,
                        reminder.service_reminder_date
                      ),
                  }}
                >
                  <div className="vehicle-header">
                    <table style={{ width: "100%", textAlign: "left" }}>
                      <tbody>
                        {/* Vehicle and Status */}
                        <tr>
                          <td
                            style={{
                              width: "70%",
                              fontWeight: "bold",
                              fontSize: "16px",
                            }}
                            onClick={() => handleEditReminder(reminder)}
                          >
                            {reminder.vehicle_reg_no || ""}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              width: "30%",
                              fontWeight: "500",
                              color: getStatusColor(
                                reminder.service_reminder_status,
                                reminder.service_reminder_date
                              ),
                              fontSize: "13px",
                            }}
                          >
                            {getStatusText(
                              reminder.service_reminder_status,
                              reminder.service_reminder_date
                            )}
                          </td>
                        </tr>

                        {/* Customer Info */}
                        <tr>
                          <td
                            colSpan="2"
                            style={{
                              fontSize: "14px",
                              color: "#2c3e50",
                              padding: "4px 0",
                            }}
                            onClick={() => handleEditReminder(reminder)}
                          >
                            <i
                              className="fas fa-user"
                              style={{ marginRight: "8px" }}
                            ></i>
                            {reminder.customer_name || "N/A"}
                            {reminder.customer_mobile && (
                              <a href={`tel:${reminder.customer_mobile}`}>
                                {" "}
                                <u>({reminder.customer_mobile})</u>
                              </a>
                            )}
                          </td>
                        </tr>

                        {/* Reminder Date */}
                        <tr>
                          <td
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              padding: "2px 0",
                            }}
                            onClick={() => handleEditReminder(reminder)}
                          >
                            <i
                              className="fas fa-calendar-day"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Reminder Date:{" "}
                            {formatDate(reminder.service_reminder_date)}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              color: "#666",
                            }}
                            onClick={() => handleEditReminder(reminder)}
                          >
                            Created: {formatDate(reminder.c_date)}
                          </td>
                        </tr>

                        {/* Remark */}
                        {reminder.service_reminder_remark && (
                          <tr>
                            <td
                              colSpan="2"
                              style={{
                                fontSize: "13px",
                                color: "#666",
                                padding: "8px 0 4px 0",
                                fontStyle: "italic",
                              }}
                              onClick={() => handleEditReminder(reminder)}
                            >
                              <i
                                className="fas fa-sticky-note"
                                style={{ marginRight: "8px" }}
                              ></i>
                              {reminder.service_reminder_remark}
                            </td>
                          </tr>
                        )}

                        {/* Action Buttons */}
                        <tr>
                          <td colSpan="2" style={{ paddingTop: "12px" }}>
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              {/* Mark as Sent Button (only for pending reminders) */}
                              {reminder.service_reminder_status == "1" && (
                                <button
                                  onClick={() =>
                                    openSentModal(reminder)
                                  }
                                  style={{
                                    background: "#10b981",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    padding: "8px 3px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <i className="fas fa-check"></i>
                                  Mark as Sent
                                </button>
                              )}

                              {/* Share Button */}
                              <button
                                onClick={() => handleShareReminder(reminder)}
                                style={{
                                  background: "#25D366",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                }}
                              >
                                <i className="fab fa-whatsapp"></i>
                                Share
                              </button>

                              {/* Delete Button */}
                              <button
                                onClick={() => openDeleteModal(reminder)}
                                style={{
                                  background: "#dc2626",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "8px",
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                }}
                              >
                                <i className="fas fa-trash"></i>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">🔔</div>
              <div className="no-vehicles-title">
                No Service Reminders Found
              </div>
              <div className="no-vehicles-text">
                {searchText
                  ? "Try adjusting your search terms"
                  : `No service reminders found for the selected date range (${formatDate(
                      dateFilters.from_date
                    )} to ${formatDate(dateFilters.to_date)})`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Add Button */}
      <button
        className="floating-add-btn"
        onClick={handleAddReminder}
        title="Add New Reminder"
      >
        <i className="fas fa-plus"></i>
      </button>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "350px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "24px", color: "#dc2626" }}
              ></i>
            </div>

            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Delete Service Reminder
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to delete service reminder for{" "}
              <strong>{deleteModal.reminder?.vehicle_reg_no}</strong>? This
              action cannot be undone.
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteReminder}
                disabled={deleteLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {deleteLoading == deleteModal.reminder?.service_reminder_id ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Sent Confirmation Modal */}
      {sentModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "350px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#ecfdf5",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-check-circle"
                style={{ fontSize: "24px", color: "#10b981" }}
              ></i>
            </div>

            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Mark as Sent
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to mark the service reminder for{" "}
              <strong>{sentModal.reminder?.vehicle_reg_no}</strong> as sent?
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeSentModal}
                disabled={sentLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmMarkAsSent}
                disabled={sentLoading !== null}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {sentLoading == sentModal.reminder?.service_reminder_id ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Marking...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
          position: relative;
        }

        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .content-area {
          padding-top: 140px; /* Increased for two rows of filters */
          padding-bottom: 80px;
        }

        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }

        .input-group.same-line {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          background: #f8f9fa;
        }

        .date-separator {
          color: #718096;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
        }

        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .reset-btn {
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 10px 12px;
          min-width: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .default-indicator {
          margin-top: 8px;
          text-align: center;
        }

        .default-badge {
          background: #e3f2fd;
          color: #0352a1;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: inline-flex;
          align-items: "center";
          gap: 6px;
        }

        .default-badge i {
          font-size: 14px;
        }

        .home-vehicles {
          padding: 20px 10px;
        }

        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .vehicles-list {
          display: grid;
          gap: 15px;
        }

        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #0352a1;
          transition: all 0.3s ease;
        }

        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .vehicle-header {
          width: 100%;
        }

        .vehicle-header table {
          width: 100%;
          border-collapse: collapse;
        }

        .vehicle-header td {
          padding: 4px 0;
          vertical-align: top;
        }

        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 0px 0;
        }

        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .floating-add-btn {
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: 60px;
          height: 60px;
          background: #0352a1;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(3, 82, 161, 0.3);
          z-index: 99;
          transition: all 0.3s ease;
          border: none;
        }

        .floating-add-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(3, 82, 161, 0.4);
        }

        .floating-add-btn:active {
          transform: scale(0.95);
        }

        @media (max-width: 768px) {
          .content-area {
            padding-top: 160px; /* More space for mobile */
          }

          .home-vehicles {
            padding: 15px 8px;
          }

          .vehicle-card {
            padding: 12px;
          }

          .vehicle-header td {
            font-size: 14px;
          }

          .sticky-filter {
            padding: 6px 0;
          }

          .input-group.same-line {
            flex-wrap: wrap;
          }

          .search-input {
            min-width: 120px;
          }

          .date-separator {
            display: none;
          }

          .floating-add-btn {
            bottom: 70px;
            right: 15px;
            width: 56px;
            height: 56px;
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .content-area {
            padding-top: 110px;
          }

          .sticky-filter {
            padding: 4px 0;
          }

          .input-group.same-line {
            gap: 6px;
          }

          .search-input {
            font-size: 13px;
            padding: 8px 10px;
          }

          .search-btn,
          .reset-btn {
            padding: 8px 10px;
            min-width: 36px;
          }

          .floating-add-btn {
            bottom: 60px;
            right: 12px;
            width: 52px;
            height: 52px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default ServiceReminderList;